import SharedPreferences from "../../constants/SharedPreferences";
import {
  getErrorMessage,
  saveInStorage,
  getFromStorage,
  getSigningCurveFromStorage,
  generateHMacHash,
  logApiResponse,
} from "../../util/Utility";
import { getNonAuthRestClientForAdmin } from "../RestUtility";
import { aeadEncryptWithAssociatedData } from "../../crypto/AuthenticatedEncryption";
import BayunError from "../../constants/BayunError";
import Constants from "../../constants/Constants";
import {
  appendSignatureAndMetadata,
  getTransactionIdContext,
  retrieveAndverifyLastSignature,
  signData,
} from "../../util/SignatureUtil";
import SigningPublicKeyTags from "../../constants/SigningPublicKeyTags";
import { encodeKey } from "../../crypto/PBKDF2";
import { hmacSha256Raw, asRawKey } from "../../crypto/KeyMaterial";
export async function updateUserRecoveryFlow(sessionId, failureCallback) {
  if (await getFromStorage(sessionId, SharedPreferences.RECOVERY_FLOW) === "SetCredentials") {
    // Call API to get values for recovery flow
    const restClient = await getNonAuthRestClientForAdmin(sessionId);
    const recoveryFlowResponse = await restClient.get("/admin/userRecoveryFlow");

    await logApiResponse("userRecoveryFlowResponse", recoveryFlowResponse);

    // The server returns an empty response once no recovery transaction is pending, which is
    // the case after setSecurityQuestions has already completed recovery. Only the local flag
    // still claims otherwise, so clear it instead of trying to complete the flow again.
    if (
      !recoveryFlowResponse.data.transactionId ||
      !recoveryFlowResponse.data.signedTransactionId
    ) {
      await saveInStorage(sessionId, SharedPreferences.RECOVERY_FLOW, null);
      return;
    }

    // verify signedTransactionId
    if (
      !(await retrieveAndverifyLastSignature(
        sessionId,
        recoveryFlowResponse.data.signedTransactionId,
      ))
    ) {
      console.log("signedTransactionId verification failed.");
      failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
      return;
    }
    let signedTransactionId = recoveryFlowResponse.data.signedTransactionId;
    const orgKey = asRawKey(
      await getFromStorage(sessionId, SharedPreferences.ORG_KEY),
    );
    const orgName = await getFromStorage(sessionId, SharedPreferences.ORG_NAME);
    const orgMemberId = await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID);
    const lmsTransactionKey = await hmacSha256Raw(
      orgKey,
      "lms" + orgName,
    );
    const transactionStatus = await aeadEncryptWithAssociatedData(
      Constants.TRANSACTION_STATUS.COMPLETED,
      "lms" + orgName,
      lmsTransactionKey,
    );

    const memberSigningPrivateKey = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_SIGNING_PRIVATE_KEY,
    );
    if (!memberSigningPrivateKey) {
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }


    const transactionId = recoveryFlowResponse.data.transactionId
    const transactionContext = await getTransactionIdContext(
      transactionId,
      Constants.TRANSACTION_STATUS.COMPLETED,
      Constants.TRANSACTION_LABEL.RECOVERY_FLOW,
      orgName,
      orgMemberId,
    );

    const newSignedTransactionId = await signData(
      transactionId,
      memberSigningPrivateKey,
      transactionContext,
      await getSigningCurveFromStorage(sessionId),
      sessionId,
    );
    signedTransactionId = await appendSignatureAndMetadata(
      signedTransactionId,
      newSignedTransactionId,
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
      transactionContext,
    );

    // Update recovery flow on server
    try {
      const updateRecoveryFlowResponse = await restClient.post(
        "/admin/userRecoveryFlow",
        {
          transactionId,
          signedTransactionId,
          transactionStatus,
          lmsTransactionKey: encodeKey(lmsTransactionKey),
        },
      );
      await logApiResponse(
        "updateUserRecoveryFlowResponse",
        updateRecoveryFlowResponse,
      );
      await saveInStorage(sessionId, SharedPreferences.RECOVERY_FLOW, null);
    } catch (error) {
      console.log("updateUserRecoveryFlow failed.", error);
      failureCallback(
        getErrorMessage(error) || BayunError.ERROR_SOMETHING_WENT_WRONG,
      );
    }
  }
}
